import Head from "next/head";
import { useRouter } from "next/router";
import { FooterProps } from "@lib/models/layout/footer";

/**
 * Site-wide MedicalBusiness / Physiotherapy structured data (items 5/20, plan
 * §2 Τεχνικά) — rendered on every page from site-settings, so the phone,
 * address and hours in the markup can never disagree with the ones on screen.
 *
 * DELIBERATELY ABSENT: `aggregateRating` (D38). Marking up Google reviews the
 * site neither collected nor hosts as its own rating is against Google's
 * structured-data guidelines and a manual-action risk — which would land on
 * the Ads push this markup exists to support. Address, hours and phone ship;
 * the rating claim does not.
 *
 * openingHoursSpecification is structured rather than the free-text
 * `workingHours` string, because the string is display copy ("9πμ-9μμ…") and
 * schema.org wants machine-readable hours.
 */
export default function BusinessJsonLd({ footer }: { footer?: FooterProps }) {
  const { asPath } = useRouter();
  if (!footer?.telephone) return null;

  // WebSite + SearchAction, HOMEPAGE ONLY (schema.org wants it once per
  // site): declares the internal search so Google may show a sitelinks
  // search box. The /search page reads ?q= (P-SEARCH).
  const isHome = asPath.split(/[?#]/)[0] === "/";
  const website = isHome ? {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Physiowave Therapy",
    url: "https://myphysiowave.gr",
    potentialAction: {
      "@type": "SearchAction",
      target: { "@type": "EntryPoint", urlTemplate: "https://myphysiowave.gr/search?q={search_term_string}" },
      "query-input": "required name=search_term_string",
    },
  } : null;

  const data = {
    "@context": "https://schema.org",
    "@type": ["MedicalBusiness", "Physiotherapy"],
    name: "Physiowave Therapy",
    legalName: footer.legalName ?? "Physiowave Therapy — Μαρίνα Παπακωνσταντοπούλου",
    url: "https://myphysiowave.gr",
    telephone: footer.telephone.replace(/\s/g, ""),
    email: footer.email,
    address: {
      "@type": "PostalAddress",
      streetAddress: footer.addressStreet,
      addressLocality: footer.addressLocality,
      postalCode: footer.addressPostalCode,
      addressCountry: "GR",
    },
    ...(footer.googleMapsUrl ? { hasMap: footer.googleMapsUrl } : {}),
    // structured hours from site-settings (P8p — admin-editable at last);
    // the old constants remain the fallback so half-filled settings never
    // strip the hours from the schema
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: footer.hoursDays
          ? footer.hoursDays.split(",").map((d) => d.trim()).filter(Boolean)
          : ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        opens: footer.hoursOpens || "09:00",
        closes: footer.hoursCloses || "21:00",
      },
    ],
    ...(footer.googleBusinessProfileUrl ? { sameAs: [footer.googleBusinessProfileUrl] } : {}),
  };

  return (
    <Head>
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
      />
      {website ? (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(website) }}
        />
      ) : null}
    </Head>
  );
}
